"use client";

import clsx from "clsx";
import { ReactNode, useState } from "react";
import { AlertIcon, CloseIcon, InfoIcon } from "../Icons/Icons";

interface AlertBannerProps {
  type?: "info" | "warning" | "error" | "infoPink";
  className?: string;
  children?: ReactNode;
  title?: string;
  description?: string;
  closable?: boolean;
  onClose?: () => void;
}

const AlertBanner = ({
  type = "info",
  className,
  children,
  title,
  description,
  closable = false,
  onClose,
}: AlertBannerProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const alertStyles = {
    info: {
      background: "bg-[#D1F1FF]",
      icon: <InfoIcon />,
    },
    warning: {
      background: "bg-[#FFF4CC]",
      icon: <InfoIcon />,
    },
    error: {
      background: "bg-[#FFE6E6]",
      icon: <AlertIcon />,
    },
    infoPink: {
      background: "bg-[#FCE5FF]",
      icon: null,
    },
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={clsx(
        "rounded-[1.6rem] p-6 flex flex-col md:flex-row md:items-center gap-4",
        "border border-[#232B37]/25",
        alertStyles[type].background,
        className,
      )}
      role="alert"
    >
      {closable && (
        <div className={clsx("flex items-center justify-end md:hidden")}>
          <button
            onClick={handleClose}
            className={clsx(
              "flex items-center justify-center",
              "w-4 h-4 rounded-full",
              "hover:bg-black/10 transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-black/20",
            )}
            aria-label="Close alert"
            type="button"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      <div className={clsx("flex items-start gap-4 md:flex-grow md:flex-row")}>
        {alertStyles[type].icon && (
          <div className={clsx("flex-shrink-0 flex items-center")}>
            {alertStyles[type].icon}
          </div>
        )}
        <div className={clsx("flex-grow flex flex-col")}>
          {children || (
            <>
              <h2 className="block font-bold">{title}</h2>
              <p className="block text-sm">{description}</p>
            </>
          )}
        </div>
      </div>

      {closable && (
        <button
          onClick={handleClose}
          className={clsx(
            "hidden md:flex flex-shrink-0 items-center justify-center",
            "w-4 h-4 rounded-full",
            "hover:bg-black/10 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-black/20",
          )}
          aria-label="Close alert"
          type="button"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

export default AlertBanner;
